import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { getNews, getNewsLog, updateNewLog } from 'services/news.service';

import './styles.scss';
import { UserInfo } from 'types/user';
import { readOncePerSession, SESSION_KEYS, setSessionValue } from 'utils/session-reads';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  send: () => void;
  player: UserInfo | null | undefined;
}

export default function NewFeatures(data: Props) {
  const { send, player } = data;
  const [news, setNews] = useState<any>();
  // Guardas em ref, e não em estado: o React 18 pode renderizar o pai de novo antes de aplicar o
  // estado deste componente, e com a guarda em estado `loadNews` passava mais de uma vez.
  const loadingRef = useRef(false);
  const startedRef = useRef(false);
  // O Dashboard passa `send` inline (função nova a cada render); a ref evita refazer a carga.
  const sendRef = useRef(send);
  sendRef.current = send;

  const { user } = useAuth();
  const userId = user?._id;
  const hasPlayer = !!player;
  const cadMissao = player?.cad_missao;

  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const handleCloseTutorialRef = useRef<() => void>(() => {});

  // A novidade é decidida 1 vez por sessão (DCE-37): a remontagem usa o resultado guardado e,
  // sem novidade, chama `send` de novo, que é o sinal `acceptSettled` da fila (DCE-39). Depois de
  // fechar (`fresh`), a próxima leitura é nova e o resultado dela substitui o da sessão.
  const loadNews = useCallback(
    async (fresh = false) => {
      if (loadingRef.current || !userId || !hasPlayer) return;

      loadingRef.current = true;
      startedRef.current = true;

      const key = SESSION_KEYS.news(userId);
      const fetchNews = async () => {
        const log = await getNewsLog();
        return (await getNews(cadMissao as number, log?.time)) ?? null;
      };

      try {
        const newsData = fresh ? await fetchNews() : await readOncePerSession(key, fetchNews);
        if (fresh) setSessionValue(key, newsData);

        if (newsData) {
          setNews(newsData);
        } else {
          sendRef.current();
        }
      } catch (error) {
        console.error('Erro ao carregar novidades:', error);
      } finally {
        loadingRef.current = false;
      }
    },
    [userId, hasPlayer, cadMissao],
  );

  const handleCloseTutorial = useCallback(async () => {
    if (!userId) return;
    setNews(undefined);
    // Fechada não reabre (DCE-38), nem se a Home desmontar antes da próxima leitura chegar.
    setSessionValue(SESSION_KEYS.news(userId), null);

    await updateNewLog(userId, news?.created);

    loadNews(true);
  }, [loadNews, news?.created, userId]);

  handleCloseTutorialRef.current = handleCloseTutorial;

  useEffect(() => {
    if (startedRef.current) return;
    loadNews();
  }, [loadNews]);

  useEffect(() => {
    window.addEventListener('close-news', handleCloseTutorial);
    return () => {
      window.removeEventListener('close-news', handleCloseTutorial);
    };
  }, [handleCloseTutorial]);

  // Focus trap
  useEffect(() => {
    if (!news) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const rafId = requestAnimationFrame(() => {
      const container = dialogRef.current;
      if (!container) return;
      const first = container.querySelectorAll<HTMLElement>(FOCUSABLE)[0] ?? container;
      first.focus();
    });

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleCloseTutorialRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const container = dialogRef.current;
      if (!container) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) { e.preventDefault(); return; }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (!container.contains(document.activeElement) || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!container.contains(document.activeElement) || document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function handleFocusIn(e: FocusEvent) {
      const container = dialogRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) {
        const first = container.querySelectorAll<HTMLElement>(FOCUSABLE)[0] ?? container;
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [news]);

  if (!news) return null;

  return (
    <div className="news-modal fixed left-0 top-0 z-50 flex h-screen w-screen justify-center overflow-y-auto bg-[rgba(0,45,75,0.62)] p-6 pt-[10vh]">
      {/* A caixa mantém os 600px e os 40px de respiro: as novidades cadastradas encostam na borda
          dela com margem negativa de 40px. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Novidades"
        tabIndex={-1}
        className="relative h-fit max-w-[600px] overflow-hidden rounded-[24px] bg-brand-white text-content shadow-[0_24px_60px_rgba(0,45,75,0.35)] outline-none"
      >
        <button
          type="button"
          onClick={() => handleCloseTutorial()}
          aria-label="Fechar novidades"
          className="absolute right-2.5 top-2.5 z-50 flex h-7 w-7 items-center justify-center rounded-full bg-brand-white text-brand-blue shadow-jj-02 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark"
        >
          <FiX size={15} strokeWidth={2.8} aria-hidden="true" />
        </button>

        {/* Peça de largura fixa maior que a tela (celular) rola aqui dentro, sem levar o botão de fechar. */}
        <div className="overflow-x-auto p-10">{news?.text && <div dangerouslySetInnerHTML={{ __html: news.text }}></div>}</div>
      </div>
    </div>
  );
}
